// ─── Test code shown on a form field (z8nrz7g2wm) ────────────────────────────
//
// A field connected to a test code shows that code beneath it, so whoever reads
// the form knows what the value represents, whether it came from the LIS or was
// typed in. The code is a LABEL only: which value of a result lands in which
// field is still the integration's mapping, and a result of any test may fill
// the field.
//
// What a field shows is the code its value was RECEIVED with when the LIS wrote
// the current value, else the code it is connected to in the form builder. The
// received code comes from the field's audit trail: the backend writes a LIS
// value with actor `integration:lis` and a reason that starts `LIS <testCode>/`
// (`writeOne` in cgt-backend src/integrations/lis/lis-mapping.service.ts). A
// later edit by a person means the value no longer is the received one.
//
// Pure on purpose: `vitest.config.ts` matches only `src/**/*.test.ts`.

import type { AuditTrailRow } from '@/types/audit'

/** The actor the backend records on a value the LIS wrote. */
export const LIS_ACTOR = 'integration:lis'

const LIS_REASON = /^LIS ([^/\s]+)\//

type FieldWrite = Pick<AuditTrailRow, 'source' | 'eventType' | 'changedBy' | 'changedAt' | 'reason'> & {
  fieldId?: string | null
}

/**
 * The test code each field's current value was received with, for the fields
 * whose latest change was the LIS's. Keyed by field id or table cell key.
 */
export function receivedTestCodes(rows: readonly FieldWrite[]): Map<string, string> {
  const latest = new Map<string, FieldWrite>()
  for (const row of rows) {
    if (row.source !== 'form' || row.eventType !== 'field_change' || !row.fieldId) continue
    const seen = latest.get(row.fieldId)
    if (!seen || Date.parse(row.changedAt) > Date.parse(seen.changedAt)) latest.set(row.fieldId, row)
  }
  const out = new Map<string, string>()
  for (const [fieldId, row] of latest) {
    if (row.changedBy !== LIS_ACTOR) continue
    const code = LIS_REASON.exec(row.reason ?? '')?.[1]
    if (code) out.set(fieldId, code)
  }
  return out
}

export interface ShownTestCode {
  code: string
  /** The current value came from the LIS with this code. */
  received: boolean
}

/** The code a field shows: the one its value was received with, else the one it is connected to. */
export function shownTestCode(
  connected: string | null | undefined,
  received: string | null | undefined,
): ShownTestCode | null {
  if (received) return { code: received, received: true }
  if (connected) return { code: connected, received: false }
  return null
}

/** The words for a shown code, used beneath a field and as a cell's hover title. */
export function testCodeText(shown: ShownTestCode): string {
  return shown.received ? `Test code: ${shown.code} (received from LIS)` : `Test code: ${shown.code}`
}
